// ─────────────────────────────────────────────────────────────────────────────
// PricingPage, the published price list.
//
// The strategic point of this page is that the number is on it. Most back-office
// software in this category is quote-only, so the page is built to answer the
// buyer's question before they have to ask a person: a hero that states the two
// rates, an interactive location count that recomputes the monthly total, a full
// "everything included" table proving the price covers the whole platform, a
// comparison band setting published pricing against quote-only buying, the way
// the bill is actually assembled, and a pricing FAQ.
//
// Billing is arranged with a person rather than self-serve, so every call to
// action is "Start free" or "Talk to us". There is no card form anywhere.
//
// The product has no photography yet, so every image slot is a <MediaSlot>: a
// flat grey panel captioned with the asset that belongs there.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    BTN_SECONDARY,
    BTN_MD,
    BTN_MD_SECONDARY,
    BTN_ON_DARK,
    BTN_ON_DARK_SOFT,
    NUMS,
    Icon,
    Container,
    Section,
    Heading,
    CardTitle,
    Lead,
    Body,
    Key,
    Badge,
    DarkPanel,
    MediaSlot,
    SectionHead,
    FeatureRow,
    FeatureGrid,
    Accordion,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── The published rates ──────────────────────────────────────────────────────
// Three published tiers. Starter covers one store, Growth covers up to five and
// then bills a flat rate per extra location, and an estate that needs something
// built is priced in a conversation. Annual billing is two months free.
// These numbers are the published price list. If they change here they must
// change on the front page plan card and in scripts/prerender.js too.
const STARTER = { monthly: 149, annual: 124, included: 1 };
const GROWTH = { monthly: 399, annual: 332, included: 5 };
const EXTRA_LOCATION = 49;

const PLANS = [
    {
        id: 'single',
        name: 'Starter',
        price: `$${STARTER.monthly}`,
        annualPrice: `$${STARTER.annual}`,
        unit: 'per month',
        blurb: 'One store, one till, the whole platform behind it.',
        points: [
            'Every module, from the daily close to wholesale receivables',
            'All five companion apps on iPad',
            'Unlimited registers and unlimited staff accounts',
            'Onboarding, with your first close run alongside you',
        ],
        cta: { label: 'Start free', to: '/signup' },
    },
    {
        id: 'multi',
        name: 'Growth',
        price: `$${GROWTH.monthly}`,
        annualPrice: `$${GROWTH.annual}`,
        unit: 'per month',
        badge: 'Up to five locations',
        blurb: 'Every store closes its own day, and the numbers roll up together.',
        points: [
            'Everything in Starter, at up to five locations',
            'One login, with the day closed where it happened',
            'Roll-ups across stores, and roles scoped to one store',
            `Extra locations beyond five are $${EXTRA_LOCATION} each per month`,
        ],
        cta: { label: 'Start free', to: '/signup' },
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        price: 'By conversation',
        custom: true,
        blurb: 'Large estates, or something that has to be built to fit.',
        points: [
            'A group of businesses run as separate workspaces',
            'Additional data sources connected on request',
            'History brought across from the system you are leaving',
            'A named contact through setup and after it',
        ],
        cta: { label: 'Talk to us', to: '/contact' },
    },
];

// ── Everything included, the proof that the price is the whole product ───────
// Every row is true for every plan. That is the point of printing it.
const INCLUDED = [
    { group: 'Daily close', covers: 'One close per location per day, owner approval, and a day that locks once it is signed off.' },
    { group: 'Cash accountability', covers: 'Blind drawer and safe counts, a written reason forced on a short drawer, over and short by person, shift and store.' },
    { group: 'Bank statements', covers: 'Statement import from CSV with duplicate rejection, and an actual deposits ledger beside it.' },
    { group: 'Processor settlement', covers: 'Card transactions and settlement batches read in and tied to the day they belong to.' },
    { group: 'Reconciliation', covers: 'Register takings, processor batches and bank deposits matched three ways, every night.' },
    { group: 'Exceptions', covers: 'Everything that did not tie out in one queue, with the location, the source, the date and the amount.' },
    { group: 'Refunds and approvals', covers: 'Mandatory reasons, approval thresholds, and a permanent audit log of every manual change.' },
    { group: 'Tenders and liabilities', covers: 'Gift certificate liability, a coupon builder, and EBT and SNAP carried as their own ledger.' },
    { group: 'Bookkeeping export', covers: 'Journal entries exported as CSV in the shape QuickBooks already imports.' },
    { group: 'Sales analytics', covers: 'Channel, location, product, hour, staff, customer, repeat rate and lifetime value.' },
    { group: 'Campaigns', covers: 'Traffic joined to the orders and the revenue it actually produced, not to a click count.' },
    { group: 'Inventory', covers: 'An append-only movement ledger with on hand derived rather than typed, plus reorder points, PAR and vendors.' },
    { group: 'Purchasing and receiving', covers: 'A reorder board that prefills the purchase order, and iPad receiving with temperature checks, variances and sign-off.' },
    { group: 'Production and labels', covers: 'Scale labels become production records, with shelf labels, barcode formats, raw materials, yield and waste.' },
    { group: 'Wholesale', covers: 'Automatic invoicing, ten price levels, per-customer overrides, partial payments, credit memos and aged receivables.' },
    { group: 'Devices and apps', covers: 'Register, scale, packing, receiving and delivery on iPad, with fleet health and remote configuration.' },
    { group: 'People and access', covers: 'Staff PINs, per-app access, single-use codes, roles across twenty modules, collaborator invites and view-as.' },
    { group: 'Assistant', covers: 'Answers drawn from your own data with the numbers computed, and an order agent that drafts a priced order.' },
    { group: 'Everywhere', covers: 'More than one workspace on one login, and CSV, Excel and branded PDF export from every table.' },
];

// ── What the subscription does not meter ─────────────────────────────────────
const NOT_CHARGED = [
    { icon: 'desktop', title: 'Registers', body: 'Add a till or another iPad and the bill does not move. Charging by device only teaches an operator to run fewer of them.' },
    { icon: 'users', title: 'Staff accounts', body: 'Everyone who works there gets a login, a PIN and the access their role allows. Seats are not a line item.' },
    { icon: 'wallet', title: 'Your sales', body: 'A flat monthly subscription, never a percentage. Wavelength does not sit between you and your payout and adds nothing to the rates you negotiated.' },
    { icon: 'phone', title: 'The apps', body: 'Register, scale, packing, receiving and delivery are part of the platform, not a second invoice at the end of the year.' },
    { icon: 'bolt', title: 'Onboarding', body: 'We connect the first store, set the locations and the roles, and run the first close with you. Included in every plan.' },
    { icon: 'sparkle', title: 'Your data', body: 'Every table exports to CSV, Excel or a branded PDF from the page you are looking at. Leaving is a download, not a negotiation.' },
];

// ── Published against quote-only, on process rather than on price ────────────
// No competitor is named and no competitor price is asserted. The contrast is
// between a price you can read today and a price that arrives after a call.
const PUBLISHED = [
    'The number is on the page, so you can budget this afternoon',
    'One rate per location, whatever your volume',
    'Every module included, so the quote cannot grow later',
    'You compare us before you ever speak to us',
    'Month to month, with no year committed in advance',
];

const QUOTED = [
    'The number arrives after a discovery call',
    'The bill is assembled from modules, seats and terminals',
    'Two vendors means two sales cycles before anything is comparable',
    'What you pay can depend on how the negotiation goes',
    'A term is often agreed before a single day has been closed',
];

// ── Pricing FAQ ──────────────────────────────────────────────────────────────
const FAQ = [
    {
        q: 'Can I see the price without talking to anyone?',
        a: `You are looking at it. Starter is $${STARTER.monthly} a month for one location, or $${STARTER.annual} a month billed annually. Growth is $${GROWTH.monthly} a month for up to five locations, or $${GROWTH.annual} billed annually, and extra locations beyond five are $${EXTRA_LOCATION} each per month. Large estates and bespoke integrations are priced in a conversation, and even then this page is the starting point.`,
    },
    {
        q: 'What does the price include?',
        a: 'The whole platform. Daily close, cash control, reconciliation, exceptions, inventory, purchasing, receiving, production, labels, wholesale, analytics, devices, staff and roles, plus the five companion iPad apps and onboarding. Registers and staff accounts are unlimited. There is no tier that unlocks reconciliation later.',
    },
    {
        q: 'Do you take a percentage of my sales?',
        a: 'No. Wavelength is a flat monthly subscription. It never sits between you and your payout, and it adds nothing on top of the processing rates you have already negotiated. It reads what happened and proves it, which is a different job from moving the money.',
    },
    {
        q: 'What counts as a location?',
        a: 'One store that closes its own till. Online orders, delivery and events roll into the location that fulfills them, so a website is not a second location. Once you run two stores or more, every location moves to the per location rate.',
    },
    {
        q: 'How do I actually pay?',
        a: 'Billing is arranged with a person rather than a card form. You can start free, connect a store and close a day before any of that. When you decide to keep it, we set the plan up by hand and invoice monthly.',
    },
    {
        q: 'What happens when I open or close a store?',
        a: 'Tell us and the count changes on the next invoice. Adding a second store moves the whole account to the per location rate. Closing one takes it off the bill and leaves its history and its closed days exactly where they are.',
    },
    {
        q: 'Is there a setup fee?',
        a: 'No. Connecting the first store, setting up locations and roles, importing a bank statement and running the first close alongside you is part of the price, not a project quoted separately.',
    },
    {
        q: 'Do I have to replace my point of sale?',
        a: 'No. Wavelength is the back office, not the register. It works alongside whatever each store already runs, in store and online, and new sources are added on request. Your staff keep ringing sales the way they do today.',
    },
    {
        q: 'Can I get my data out?',
        a: 'Yes. Every table exports to CSV, Excel or a branded PDF from the page you are on, and the movement ledger and audit log are append-only, so the record you export is the record that was kept.',
    },
];

// ── A bullet in a plan card ──────────────────────────────────────────────────
function Tick({ children }) {
    return (
        <li className="flex items-start gap-3 text-[14px] leading-[1.55] text-[#3D3D3D]">
            <span className="mt-[3px] flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-[#0A0A0A] text-white">
                <Icon name="check" className="h-3 w-3" strokeWidth={2.2} />
            </span>
            <span>{children}</span>
        </li>
    );
}

// A filled cell in the included table. Every cell in that table is one of these.
function Included() {
    return (
        <span role="img" aria-label="Included" className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F5F5F5] text-[#0A0A0A]">
            <Icon name="check" className="h-[13px] w-[13px]" strokeWidth={2.2} />
        </span>
    );
}

// ── The location count, and the plan board it drives ─────────────────────────
// The count is the only control on the page. It picks the applicable plan and
// recomputes the monthly total, so the buyer answers their own question.
function PlanBoard() {
    const [count, setCount] = useState(3);
    const [annual, setAnnual] = useState(false);

    const { multi, extras, monthly } = useMemo(() => {
        const isMulti = count >= 2;
        const base = isMulti ? (annual ? GROWTH.annual : GROWTH.monthly) : (annual ? STARTER.annual : STARTER.monthly);
        const over = isMulti ? Math.max(0, count - GROWTH.included) : 0;
        return { multi: isMulti, extras: over, monthly: base + over * EXTRA_LOCATION };
    }, [count, annual]);

    const priceNoteFor = (plan) => {
        if (plan.id === 'single') return 'One location, billed monthly or annually.';
        if (plan.id === 'multi') {
            if (!multi) return 'The tier as soon as there are two stores on the account.';
            return extras > 0
                ? `${count} locations is the tier plus ${extras} extra, ${'$'}${monthly.toLocaleString('en-US')} a month.`
                : `${count} locations is inside the tier, ${'$'}${monthly.toLocaleString('en-US')} a month.`;
        }
        return 'Scoped with you, then priced in writing.';
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-6 rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
                <div className="max-w-[340px]">
                    <CardTitle>How many locations do you run?</CardTitle>
                    <Body className="mt-2">Registers, staff accounts and the companion apps are unlimited at every count.</Body>
                    <div className="mt-4 inline-flex items-center gap-1 rounded-full border border-[rgba(10,10,10,0.10)] p-1">
                        {[['Monthly', false], ['Annual', true]].map(([label, v]) => (
                            <button
                                key={label}
                                type="button"
                                onClick={() => setAnnual(v)}
                                className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors ${annual === v ? 'bg-[#0A0A0A] text-white' : 'text-[#6B6B6B] hover:text-[#0A0A0A]'}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-5 sm:gap-8">
                    <div className="flex items-center gap-1 rounded-full border border-[rgba(10,10,10,0.10)] p-1">
                        <button
                            type="button"
                            aria-label="Fewer locations"
                            onClick={() => setCount((c) => Math.max(1, c - 1))}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#6B6B6B] transition-colors hover:bg-[#F5F5F5] hover:text-[#0A0A0A]"
                        >
                            <Icon name="minus" className="h-4 w-4" />
                        </button>
                        <span className={`w-9 text-center text-[16px] font-semibold text-[#0A0A0A] ${NUMS}`}>{count}</span>
                        <button
                            type="button"
                            aria-label="More locations"
                            onClick={() => setCount((c) => Math.min(60, c + 1))}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#6B6B6B] transition-colors hover:bg-[#F5F5F5] hover:text-[#0A0A0A]"
                        >
                            <Icon name="plus" className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="hidden h-12 w-px bg-[rgba(10,10,10,0.10)] sm:block" />
                    <div>
                        <div className={`text-[32px] font-semibold leading-none tracking-[-0.03em] text-[#0A0A0A] ${NUMS}`}>
                            ${monthly.toLocaleString('en-US')}
                        </div>
                        <div className="mt-2 text-[12px] leading-[1.4] text-[#A1A1A1]">
                            {annual ? 'per month, billed annually' : 'per month, billed monthly'}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {PLANS.map((plan) => {
                    const applicable = (plan.id === 'single' && !multi) || (plan.id === 'multi' && multi);
                    return (
                        <div
                            key={plan.id}
                            className={`flex flex-col rounded-[24px] border bg-white p-6 transition-colors duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] sm:p-7 ${applicable ? 'border-[#0A0A0A]' : 'border-[rgba(10,10,10,0.10)] hover:border-[rgba(10,10,10,0.30)]'}`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-[16px] font-semibold tracking-[-0.01em] text-[#0A0A0A]">{plan.name}</span>
                                {plan.badge && <Badge tone="accent">{plan.badge}</Badge>}
                            </div>
                            <p className="mt-2 text-[13px] leading-[1.5] text-[#8A8A8A]">{plan.blurb}</p>

                            <div className="mt-7">
                                {plan.custom ? (
                                    <div className="text-[28px] font-semibold leading-none tracking-[-0.025em] text-[#0A0A0A]">{plan.price}</div>
                                ) : (
                                    <div className="flex items-end gap-2">
                                        <span className={`text-[44px] font-semibold leading-none tracking-[-0.035em] text-[#0A0A0A] ${NUMS}`}>{annual && plan.annualPrice ? plan.annualPrice : plan.price}</span>
                                        <span className="mb-1.5 text-[13px] leading-[1.3] text-[#8A8A8A]">{plan.unit}</span>
                                    </div>
                                )}
                                <p className="mt-3 text-[12px] leading-[1.5] text-[#A1A1A1]">{priceNoteFor(plan)}</p>
                            </div>

                            <ul className="mt-7 flex flex-col gap-3">
                                {plan.points.map((p) => <Tick key={p}>{p}</Tick>)}
                            </ul>

                            <div className="mt-auto pt-8">
                                <Link to={plan.cta.to} className={`${plan.custom ? BTN_MD_SECONDARY : BTN_MD} w-full`}>
                                    {plan.cta.label}
                                    <Icon name="arrow" className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function PricingPage() {
    return (
        <MarketingShell active="pricing">
            <Seo
                title={`Pricing, from $${STARTER.monthly} a month`}
                description={`Published pricing, not a quote. Starter is $${STARTER.monthly} a month for one location, Growth is $${GROWTH.monthly} for up to five, and extra locations are $${EXTRA_LOCATION} each. Everything included.`}
                path="/pricing"
                keywords="back office software pricing, retail back office price per location, multi location reconciliation software cost, daily close software pricing, cash control software price, published pricing back office, POS back office subscription cost, wholesale invoicing software pricing"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'Product',
                            '@id': 'https://wavelengtherp.com/pricing#single-location',
                            name: 'Wavelength Starter',
                            description: 'Back-office and reconciliation platform for one retail or food location. Daily close, cash accountability, three-way reconciliation, inventory, production and wholesale, with unlimited registers and staff.',
                            category: 'Retail back office software',
                            brand: { '@id': 'https://wavelengtherp.com/#organization' },
                            offers: {
                                '@type': 'Offer',
                                price: String(STARTER.monthly),
                                priceCurrency: 'USD',
                                url: 'https://wavelengtherp.com/pricing',
                                availability: 'https://schema.org/InStock',
                                seller: { '@id': 'https://wavelengtherp.com/#organization' },
                                priceSpecification: {
                                    '@type': 'UnitPriceSpecification',
                                    price: String(STARTER.monthly),
                                    priceCurrency: 'USD',
                                    unitText: 'location',
                                    billingIncrement: 1,
                                    referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: 'MON' },
                                },
                            },
                        },
                        {
                            '@type': 'Product',
                            '@id': 'https://wavelengtherp.com/pricing#multi-location',
                            name: 'Wavelength Growth',
                            description: 'The same platform across up to five locations, with extra locations billed at a flat rate. Each store closes its own day and the numbers roll up on one login.',
                            category: 'Retail back office software',
                            brand: { '@id': 'https://wavelengtherp.com/#organization' },
                            offers: {
                                '@type': 'Offer',
                                price: String(GROWTH.monthly),
                                priceCurrency: 'USD',
                                url: 'https://wavelengtherp.com/pricing',
                                availability: 'https://schema.org/InStock',
                                eligibleQuantity: { '@type': 'QuantitativeValue', minValue: 2, maxValue: 5, unitText: 'location' },
                                seller: { '@id': 'https://wavelengtherp.com/#organization' },
                                priceSpecification: {
                                    '@type': 'UnitPriceSpecification',
                                    price: String(GROWTH.monthly),
                                    priceCurrency: 'USD',
                                    unitText: 'location',
                                    billingIncrement: 1,
                                    referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: 'MON' },
                                },
                            },
                        },
                        {
                            '@type': 'Product',
                            '@id': 'https://wavelengtherp.com/pricing#enterprise',
                            name: 'Wavelength, enterprise',
                            description: 'Priced in a conversation for large estates, several businesses in one account, additional data sources and migration from an existing system.',
                            category: 'Retail back office software',
                            brand: { '@id': 'https://wavelengtherp.com/#organization' },
                            offers: {
                                '@type': 'Offer',
                                priceCurrency: 'USD',
                                url: 'https://wavelengtherp.com/contact',
                                availability: 'https://schema.org/InStock',
                                seller: { '@id': 'https://wavelengtherp.com/#organization' },
                                description: 'Quoted after scoping. The published tiers are the starting point.',
                            },
                        },
                        {
                            '@type': 'FAQPage',
                            mainEntity: FAQ.map((f) => ({
                                '@type': 'Question',
                                name: f.q,
                                acceptedAnswer: { '@type': 'Answer', text: f.a },
                            })),
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'Pricing', item: 'https://wavelengtherp.com/pricing' },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Pricing"
                title="The price is on the page."
                lead={<><Key>${STARTER.monthly} a month</Key> for one location. <Key>${GROWTH.monthly} a month</Key> for up to five, then ${EXTRA_LOCATION} for each one after that. Every module, every companion app, unlimited registers and unlimited staff, in all of them.</>}
                primary={{ label: 'Start free', to: '/signup' }}
                secondary={{ label: 'Talk to us', to: '/contact' }}
            >
                <Reveal>
                    <div className="flex flex-wrap gap-2">
                        {['No setup fee', 'No per-register charge', 'No per-seat charge', 'No percentage of your sales', 'Onboarding included'].map((t) => (
                            <Badge key={t}>{t}</Badge>
                        ))}
                    </div>
                </Reveal>
            </PageHero>

            {/* ── Plans and the location count ───────────────────────────── */}
            <Section id="plans" pad="md">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Priced per location."
                            lead="Set the number of stores and the total is the total. There are no seat licences underneath it, no register fees beside it and no module waiting to be unlocked later."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12">
                            <PlanBoard />
                        </div>
                    </Reveal>
                    <Reveal delay={120}>
                        <p className="mt-6 text-[12px] leading-[1.6] text-[#A1A1A1]">
                            Prices in US dollars, per month. Billing is arranged with a person, so nothing is charged until you have closed a day and decided to keep it.
                        </p>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Everything included ────────────────────────────────────── */}
            <Section id="included" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="One price, the whole platform."
                            lead="This is the entire product, not a starting tier. There is no plan that unlocks reconciliation, no add-on for the companion apps, and no module that turns up on an invoice a year in."
                            actions={<Link to="/features" className={BTN_SECONDARY}>See the platform<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12 overflow-x-auto rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white">
                            <table className="w-full min-w-[820px] border-collapse text-left">
                                <thead>
                                    <tr className="border-b border-[rgba(10,10,10,0.10)] bg-[#FAFAFA]">
                                        <th className="px-6 py-4 text-[13px] font-medium text-[#0A0A0A]">Capability</th>
                                        <th className="px-6 py-4 text-[13px] font-medium text-[#0A0A0A]">What it covers</th>
                                        <th className="px-4 py-4 text-center text-[13px] font-medium text-[#0A0A0A]">Single</th>
                                        <th className="px-4 py-4 text-center text-[13px] font-medium text-[#0A0A0A]">Multi</th>
                                        <th className="px-4 py-4 text-center text-[13px] font-medium text-[#0A0A0A]">Enterprise</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {INCLUDED.map((row, i) => (
                                        <tr key={row.group} className={i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''}>
                                            <td className="px-6 py-4 align-top text-[14px] font-medium leading-[1.45] text-[#0A0A0A]">{row.group}</td>
                                            <td className="px-6 py-4 align-top text-[13.5px] leading-[1.55] text-[#6B6B6B]">{row.covers}</td>
                                            <td className="px-4 py-4 text-center align-top"><Included /></td>
                                            <td className="px-4 py-4 text-center align-top"><Included /></td>
                                            <td className="px-4 py-4 text-center align-top"><Included /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Reveal>
                    <Reveal delay={120}>
                        <p className="mt-4 text-[12px] leading-[1.6] text-[#A1A1A1]">
                            Nothing in this table is an upgrade. The three columns are there to be read as one.
                        </p>
                    </Reveal>
                </Container>
            </Section>

            {/* ── What is never metered ──────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="What you are never charged for."
                            lead="A price that grows every time you add a till, a manager or a scale is a price that quietly teaches you to run the business smaller. This one does not."
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={NOT_CHARGED} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Published against quote-only ───────────────────────────── */}
            <Section id="compare" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <DarkPanel className="p-6 sm:p-12 lg:p-14">
                            <div className="max-w-[560px]">
                                <h2 className="text-[28px] font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-[36px]">
                                    Published beats quoted.
                                </h2>
                                <p className="mt-4 text-[16px] leading-[1.6] text-white/60">
                                    Most software in this category is priced by conversation. That is not a difference of style. It decides whether you can budget before you have taken a call, and whether you can compare two options at all.
                                </p>
                            </div>

                            <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-0">
                                <div className="lg:border-r lg:border-white/[0.14] lg:pr-10">
                                    <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-white">Priced on the page</h3>
                                    <ul className="mt-5 flex flex-col gap-3.5">
                                        {PUBLISHED.map((p) => (
                                            <li key={p} className="flex items-start gap-3 text-[14px] leading-[1.55] text-white/80">
                                                <span className="mt-[3px] flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-white text-[#111111]">
                                                    <Icon name="check" className="h-3 w-3" strokeWidth={2.2} />
                                                </span>
                                                <span>{p}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="lg:pl-10">
                                    <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-white/55">Priced on a call</h3>
                                    <ul className="mt-5 flex flex-col gap-3.5">
                                        {QUOTED.map((p) => (
                                            <li key={p} className="flex items-start gap-3 text-[14px] leading-[1.55] text-white/45">
                                                <span className="mt-[3px] flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border border-dashed border-white/25 text-white/45">
                                                    <Icon name="minus" className="h-3 w-3" strokeWidth={2.2} />
                                                </span>
                                                <span>{p}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="mt-12 flex flex-col gap-5 border-t border-white/[0.14] pt-8 sm:flex-row sm:items-center sm:justify-between">
                                <p className="max-w-[440px] text-[14px] leading-[1.6] text-white/55">
                                    We are not claiming to be the cheapest line on your budget. We are claiming you can find out what it costs without booking anything.
                                </p>
                                <div className="flex flex-none flex-wrap gap-2">
                                    <Link to="/signup" className={BTN_ON_DARK}>Start free</Link>
                                    <Link to="/contact" className={BTN_ON_DARK_SOFT}>Talk to us</Link>
                                </div>
                            </div>
                        </DarkPanel>
                    </Reveal>
                </Container>
            </Section>

            {/* ── How the bill is assembled ──────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Billing"
                            title="How the bill is put together."
                            body="Billing is arranged with a person rather than a card form. You start free, tell us the stores you actually run, and the first invoice reflects the count you agreed."
                            bullets={[
                                'A location is one store that closes its own till',
                                'Online, delivery and event sales roll into the location that fulfills them',
                                'Two stores or more move every location to the per location rate',
                                'Open a store mid month and the next invoice follows the count',
                                'Close one and it comes off, with its closed days left intact',
                            ]}
                            cta={<Link to="/contact" className={BTN_SECONDARY}>Talk to us<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="The location switcher open on a workspace with three stores"
                                    note="Laptop screenshot, the store list open at the top of the page, one store selected"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Pricing FAQ ────────────────────────────────────────────── */}
            <Section id="faq" tone="paper2" pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Heading>Questions about the bill.</Heading>
                                <Lead>What a location is, what the price covers, and how the invoice is put together. If yours is not here, a person will answer it.</Lead>
                                <div className="mt-2">
                                    <Link to="/faq" className={BTN_SECONDARY}>All questions<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <Accordion items={FAQ} />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Get started"
                title="Start free. Pay once it is running."
                body="Connect one store, close one day and read the reconciliation in the morning. The plan is set up with a person after that, not with a card form before it."
                primary={{ label: 'Start free', to: '/signup' }}
                secondary={{ label: 'Talk to us', to: '/contact' }}
            />
        </MarketingShell>
    );
}
